import { ICON_CLASS } from '@prophecy/icons/tokens';
import { theme, hexToRGBA, resolveCssVariable } from '@prophecy/theme';
import styled, { css } from 'styled-components';
import { tokens } from './tokens';
import { Variant } from './types';
// Gradient utility functions
const getResolvedColor = ({ tone, variant, layer }) => {
    const colorShade = tokens.colorShades[variant][layer];
    if (colorShade === 0)
        return theme.colors.transparent;
    const toneStr = `${tone}${colorShade}`;
    const color = `var(--var-colors-${toneStr})`;
    return color;
};
const createGradientBackground = (color) => `linear-gradient(180deg, 
    ${hexToRGBA(resolveCssVariable(color), 1)}, 
    ${hexToRGBA(resolveCssVariable(color), 0)})`;
const createGradientBorder = (color) => `linear-gradient(180deg, 
    ${hexToRGBA(resolveCssVariable(color), 0.85)}, 
    ${hexToRGBA(resolveCssVariable(color), 1)})`;
export const InnerLayer = styled.div `
  position: absolute;
  width: ${({ $size }) => tokens.IconSize[$size].innerLayerSize};
  height: ${({ $size }) => tokens.IconSize[$size].innerLayerSize};
  border-radius: 50%;
  z-index: ${theme.zLayer.s};
  background: ${({ $variant, $tone }) => $variant === Variant.modern
    ? 'transparent'
    : getResolvedColor({ tone: $tone, variant: $variant, layer: 'innerLayer' })};
`;
// Variant-specific styles
const defaultIconStyle = css `
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.skeumorphicXs};

  ${InnerLayer} {
    background: ${theme.colors.transparent};
  }

  .${ICON_CLASS} {
    color: ${theme.colors.gray600};
    z-index: ${theme.zLayer.s};
  }
`;
const gradientIconStyle = css `
  background: ${({ $tone, $variant }) => createGradientBackground(getResolvedColor({ tone: $tone, variant: $variant, layer: 'outerLayer' }))};
  .${ICON_CLASS} {
    color: white;
  }
`;
const lightGradientIconStyle = css `
  background: ${({ $tone, $variant }) => createGradientBackground(getResolvedColor({ tone: $tone, variant: $variant, layer: 'outerLayer' }))};
  .${ICON_CLASS} {
    color: ${({ $tone }) => getResolvedColor({ tone: $tone, variant: Variant.dark, layer: 'outerLayer' })};
  }
`;
const lightIconStyle = css `
  background: ${({ $tone, $variant }) => getResolvedColor({ tone: $tone, variant: $variant, layer: 'outerLayer' })};

  ${InnerLayer} {
    background: ${theme.colors.transparent};
  }

  .${ICON_CLASS} {
    color: ${({ $tone }) => getResolvedColor({ tone: $tone, variant: Variant.dark, layer: 'outerLayer' })};
  }
`;
const darkIconStyle = css `
  background: ${({ $tone, $variant }) => getResolvedColor({ tone: $tone, variant: $variant, layer: 'outerLayer' })};

  ${({ $tone, $variant }) => $variant !== Variant.modern &&
    `
    &::before {
        box-sizing: border-box;
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: inherit;
        background: ${createGradientBorder(getResolvedColor({ tone: $tone, variant: Variant.gradient, layer: 'innerLayer' }))};
        box-shadow: ${theme.shadows.skeumorphicXs};
        z-index: -1;
        transition: ${theme.transition.s};
    }
    `}

  ${InnerLayer} {
    background: ${theme.colors.transparent};
  }

  .${ICON_CLASS} {
    color: ${theme.colors.white};
  }
`;
// Main component
export const StyledFeaturedIcon = styled.div `
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${({ $size }) => tokens.IconSize[$size].backgroundLayerSize};
  height: ${({ $size }) => tokens.IconSize[$size].backgroundLayerSize};

  .${ICON_CLASS} {
    width: ${({ $size }) => tokens.IconSize[$size].iconSize.default};
    height: ${({ $size }) => tokens.IconSize[$size].iconSize.default};

    ${({ $variant, $size }) => ($variant === Variant.gradient || $variant === Variant.lightGradient) &&
    `
        width: ${tokens.IconSize[$size].iconSize.gradient};
        height: ${tokens.IconSize[$size].iconSize.gradient};
      `}

    ${({ $size, $variant }) => $variant === Variant.modern &&
    `
        width: ${tokens.IconSize[$size].iconSize.default};
        height: ${tokens.IconSize[$size].iconSize.default};
      `}
    
    color: ${theme.colors.white};
    z-index: ${theme.zLayer.s};
  }

  ${({ $variant }) => {
    if ($variant === Variant.modern)
        return defaultIconStyle;
    if ($variant === Variant.gradient)
        return gradientIconStyle;
    if ($variant === Variant.light)
        return lightIconStyle;
    if ($variant === Variant.dark)
        return darkIconStyle;
    if ($variant === Variant.lightGradient)
        return lightGradientIconStyle;
}}
`;
